import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { User, Save, Camera, X, Lock, FileText } from 'lucide-react';
import { auth, authFetch } from '../services/auth';
import {
  allTechStacks,
  techStacksByCategory,
  categoryLabels,
} from '../data/techStacks';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ResumeUploadModal from '../components/ResumeUploadModal';

interface UserInfo {
  id: number;
  email: string;
  nickname: string;
  avatar: string | null;
  created_at: number;
  tech_stacks?: string[];
  resumeName?: string | null;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { message: antdMessage } = App.useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar] = useState(false);

  // 编辑状态
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null); // 头像预览
  const [avatarFile, setAvatarFile] = useState<File | null>(null); // 待上传的头像文件
  const [selectedTechStacks, setSelectedTechStacks] = useState<string[]>([]); // 选中的技术栈
  const [showTechStackModal, setShowTechStackModal] = useState(false); // 显示技术栈选择弹窗
  const [tempSelectedTechStacks, setTempSelectedTechStacks] = useState<
    string[]
  >([]); // 弹窗中的临时选择

  // 字段校验错误
  const [fieldError, setFieldError] = useState('');
  const [touched, setTouched] = useState(false);

  // 修改密码弹窗状态
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // 简历上传弹窗状态
  const [showResumeUploadModal, setShowResumeUploadModal] = useState(false);

  // 简历文件暂存状态
  const [resumeFile, setResumeFile] = useState<File | null>(null); // 待上传的简历文件
  const [resumeFileName, setResumeFileName] = useState<string>(''); // 简历文件名

  // 阻止背景页面滚动 - 技术栈弹窗
  useEffect(() => {
    if (showTechStackModal) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showTechStackModal]);

  // 从 auth 获取用户信息并监听变化
  useEffect(() => {
    const updateUserFromAuth = () => {
      const currentUser = auth.getUser();
      if (currentUser) {
        setUser({
          id: parseInt(currentUser.id),
          email: currentUser.email,
          nickname: currentUser.nickname,
          avatar: currentUser.avatar || null,
          created_at: currentUser.created_at || 0,
          tech_stacks: currentUser.tech_stacks,
          resumeName: currentUser.resumeName,
        });
        setNickname(currentUser.nickname || '');
        setAvatar(currentUser.avatar || null);
        setSelectedTechStacks(currentUser.tech_stacks || []);
        setLoading(false);
      }
    };

    // 初始加载
    updateUserFromAuth();

    // 订阅 auth 变化
    const unsubscribe = auth.subscribe(() => {
      updateUserFromAuth();
    });

    return () => unsubscribe();
  }, []);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleResumeUploadSuccess = (file: File) => {
    setResumeFile(file);
    setResumeFileName(file.name);
    antdMessage.success(`已选择简历: ${file.name}`);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      antdMessage.error('请上传图片文件');
      return;
    }

    // 检查文件大小 (最大 8MB)
    if (file.size > 8 * 1024 * 1024) {
      antdMessage.error('图片大小不能超过8MB');
      return;
    }

    // 创建预览URL，保存文件待上传
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setAvatarFile(file);

    // 清空input，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 校验昵称
  const validateNickname = (value: string): string => {
    if (!value.trim()) return '请输入昵称';
    if (value.length > 20) return '昵称长度不能超过20位';
    return '';
  };

  const handleNicknameChange = (value: string) => {
    setNickname(value);
    if (touched) {
      setFieldError(validateNickname(value));
    }
  };

  const handleNicknameBlur = () => {
    setTouched(true);
    setFieldError(validateNickname(nickname));
  };

  const handleSave = async () => {
    // 校验昵称
    const nicknameError = validateNickname(nickname);
    setFieldError(nicknameError);
    setTouched(true);

    if (nicknameError) {
      return;
    }

    setSaving(true);

    try {
      let newAvatarUrl = avatar;

      // 如果有新头像，先上传
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);

        const uploadResponse = await authFetch(
          `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/avatar`,
          {
            method: 'POST',
            body: formData,
          },
        );

        const uploadData = await uploadResponse.json();

        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || '头像上传失败');
        }

        newAvatarUrl = uploadData.avatar;

        // 清理预览URL
        if (avatarPreview) {
          URL.revokeObjectURL(avatarPreview);
        }
      }

      // 如果有简历文件，上传简历
      if (resumeFile) {
        const formData = new FormData();
        formData.append('resume', resumeFile);

        const uploadResponse = await authFetch(
          `${import.meta.env.VITE_API_BASE_URL || ''}/api/upload/resume`,
          {
            method: 'POST',
            body: formData,
          },
        );

        const uploadData = await uploadResponse.json();

        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || '简历上传失败');
        }

        // 清空简历文件状态
        setResumeFile(null);
        setResumeFileName('');
      }

      // 更新用户信息
      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/user`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nickname: nickname.trim(),
            avatar: newAvatarUrl,
            tech_stacks: selectedTechStacks,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('保存失败');
      }

      const data = await response.json();

      // 清空预览状态
      setAvatarPreview(null);
      setAvatarFile(null);

      antdMessage.success('保存成功');

      // 刷新本地用户信息
      await auth.fetchUser();
    } catch (err) {
      antdMessage.error(
        err instanceof Error ? err.message : '保存失败，请稍后重试',
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 处理修改密码成功
  const handlePasswordChangeSuccess = async () => {
    // 关闭弹窗
    setShowChangePasswordModal(false);
    // 执行退出登录
    await auth.logout();
    // 跳转到首页
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B]">
      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* 标题 */}
        <div className="relative mb-5">
          <h1 className="text-2xl font-bold text-white text-center">
            个人设置
          </h1>
        </div>

        {/* 主卡片 - 毛玻璃效果 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-10 border border-slate-700/50">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !user ? (
            <div className="text-center py-16">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                重新加载
              </button>
            </div>
          ) : user ? (
            <>
              {/* 头像区域 */}
              <div className="flex flex-col items-center mb-8">
                {/* 头像上传区域 */}
                <div
                  onClick={handleAvatarClick}
                  className="relative w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden cursor-pointer group mb-4"
                >
                  {/* 头像图片 - 优先显示预览图 */}
                  {avatarPreview || avatar ? (
                    <img
                      src={avatarPreview || avatar || ''}
                      alt="avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <User className="w-14 h-14 text-white" />
                    </div>
                  )}

                  {/* Hover遮罩层 */}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <Camera className="w-8 h-8 text-white" />
                  </div>

                  {/* 上传中状态 */}
                  {uploadingAvatar && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  {/* 边框 - hover时显示 */}
                  <div className="absolute inset-0 rounded-full ring-4 ring-transparent group-hover:ring-amber-500/50 shadow-lg shadow-transparent group-hover:shadow-amber-500/20 pointer-events-none transition-all duration-200" />
                </div>

                {/* 隐藏的文件输入 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <p className="text-slate-500 text-xs mt-1">
                  支持 JPG、PNG 格式，最大 8MB
                </p>
              </div>

              {/* 表单区域 */}
              <div className="max-w-3xl mx-auto space-y-8">
                {/* 基本信息卡片 */}
                <div className="bg-[#0F172A]/50 rounded-xl p-6 border border-slate-700/50">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-1 h-4 bg-amber-500 rounded-full"></div>
                    <h3 className="text-white text-sm font-medium">基本信息</h3>
                  </div>

                  <div className="space-y-5">
                    {/* 昵称输入框 */}
                    <div className="space-y-2">
                      <label className="block text-slate-300 text-sm font-medium">
                        昵称
                      </label>
                      <input
                        type="text"
                        value={nickname}
                        onChange={(e) => handleNicknameChange(e.target.value)}
                        onBlur={handleNicknameBlur}
                        placeholder="请输入昵称"
                        className={`w-full h-11 px-4 rounded-lg
                          text-white text-sm
                          placeholder-slate-500
                          transition-all duration-200
                          focus:outline-none
                          ${
                            fieldError && touched
                              ? 'bg-[#1E293B] border border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                              : 'bg-[#1E293B] border border-slate-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 hover:border-slate-600'
                          }`}
                      />
                      {/* 昵称校验错误提示 */}
                      {fieldError && touched && (
                        <p className="text-red-400 text-xs flex items-center gap-1.5">
                          <svg
                            className="w-3.5 h-3.5 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {fieldError}
                        </p>
                      )}
                    </div>

                    {/* 邮箱和注册时间 - 两列布局 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* 邮箱输入框 (只读) */}
                      <div className="space-y-2">
                        <label className="block text-slate-300 text-sm font-medium">
                          邮箱
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            value={user.email}
                            readOnly
                            className="w-full h-11 px-4 pr-16 bg-[#1E293B] border border-slate-700/50 rounded-lg
                              text-slate-400 text-sm
                              cursor-not-allowed"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-slate-700/70 text-slate-500 text-xs rounded">
                            只读
                          </span>
                        </div>
                      </div>

                      {/* 注册时间 */}
                      <div className="space-y-2">
                        <label className="block text-slate-300 text-sm font-medium">
                          注册时间
                        </label>
                        <div className="h-11 px-4 bg-[#1E293B] border border-slate-700/50 rounded-lg flex items-center">
                          <span className="text-slate-400 text-sm">
                            {formatDate(user.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 技能卡片 */}
                <div className="bg-[#0F172A]/50 rounded-xl p-6 border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium">技能</h3>
                        <p className="text-slate-500 text-xs mt-0.5">
                          {selectedTechStacks.length > 0
                            ? `已选择 ${selectedTechStacks.length} 个技能`
                            : '选择你熟悉的技能，帮助面试官更好地了解你'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setTempSelectedTechStacks(selectedTechStacks);
                        setShowTechStackModal(true);
                      }}
                      className="px-4 py-2 text-sm text-amber-300 hover:text-white
                        bg-amber-500/10 hover:bg-amber-500/20
                        border border-amber-500/30 hover:border-amber-500/50
                        rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      {selectedTechStacks.length > 0 ? '修改技能' : '选择技能'}
                    </button>
                  </div>

                  {selectedTechStacks.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {selectedTechStacks.map((techId) => {
                        const tech = allTechStacks.find((t) => t.id === techId);
                        return tech ? (
                          <span
                            key={techId}
                            className="group relative px-3 py-1.5
                              bg-gradient-to-r from-amber-500/20 to-orange-500/20
                              text-amber-200 text-sm rounded-md
                              border border-amber-500/30 hover:border-amber-500/50
                              transition-all duration-200"
                          >
                            {tech.name}
                            {/* 删除按钮 - 绝对定位于右上角 */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTechStacks((prev) =>
                                  prev.filter((id) => id !== techId),
                                );
                              }}
                              className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center
                                bg-slate-700/80 rounded-full text-slate-400
                                opacity-0 group-hover:opacity-70 hover:!opacity-100 transition-all cursor-pointer"
                            >
                              <svg
                                className="w-2.5 h-2.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* 简历上传入口 */}
                <div className="bg-[#0F172A]/50 rounded-xl p-6 border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium">
                          简历上传
                        </h3>
                        <p className="text-slate-500 text-xs mt-0.5">
                          {resumeFileName ||
                            user?.resumeName ||
                            '上传简历帮助面试官更好地了解你'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowResumeUploadModal(true)}
                      className="px-4 py-2 text-sm text-amber-300 hover:text-white
                        bg-amber-500/10 hover:bg-amber-500/20
                        border border-amber-500/30 hover:border-amber-500/50
                        rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      {resumeFileName || user?.resumeName
                        ? '更换简历'
                        : '上传简历'}
                    </button>
                  </div>
                </div>

                {/* 修改密码入口 */}
                <div className="bg-[#0F172A]/50 rounded-xl p-6 border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium">
                          修改密码
                        </h3>
                        <p className="text-slate-500 text-xs mt-0.5">
                          定期更换密码可以提高账号安全性
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowChangePasswordModal(true)}
                      className="px-4 py-2 text-sm text-amber-300 hover:text-white
                        bg-amber-500/10 hover:bg-amber-500/20
                        border border-amber-500/30 hover:border-amber-500/50
                        rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      修改密码
                    </button>
                  </div>
                </div>

                {/* 保存按钮区域 */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="group relative px-10 py-3 rounded-xl
                      bg-gradient-to-r from-amber-500 to-orange-500
                      text-white text-sm font-semibold
                      transition-all duration-300
                      hover:scale-[1.02]
                      hover:from-amber-400 hover:to-orange-400
                      active:scale-[0.98]
                      focus:outline-none focus:ring-2 focus:ring-amber-500/40
                      flex items-center gap-2.5
                      disabled:opacity-50 disabled:cursor-not-allowed
                      disabled:hover:scale-100
                      cursor-pointer overflow-hidden"
                  >
                    {/* 按钮背景光效 */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>保存中...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>保存修改</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* 技能选择弹窗 */}
      {showTechStackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          {/* 背景遮罩 */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />

          {/* 弹窗内容 */}
          <div className="relative w-full max-w-2xl max-h-[80vh] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden flex flex-col animate-scale-in">
            {/* 头部 */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-white font-semibold">选择技能</h3>
              <button
                onClick={() => {
                  setShowTechStackModal(false);
                  setTempSelectedTechStacks([]);
                }}
                className="text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg p-1 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
              {Object.entries(techStacksByCategory).map(([category, techs]) => (
                <div key={category}>
                  <h4 className="text-slate-400 text-sm font-medium mb-3">
                    {categoryLabels[category]}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {techs.map((tech) => {
                      const isSelected = tempSelectedTechStacks.includes(
                        tech.id,
                      );
                      return (
                        <button
                          key={tech.id}
                          onClick={() => {
                            if (isSelected) {
                              setTempSelectedTechStacks((prev) =>
                                prev.filter((id) => id !== tech.id),
                              );
                            } else {
                              setTempSelectedTechStacks((prev) => [
                                ...prev,
                                tech.id,
                              ]);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm transition-all duration-200 cursor-pointer ${
                            isSelected
                              ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-200 border border-amber-500/30'
                              : 'bg-[#0F172A] text-slate-300 border border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          {tech.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* 底部 */}
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <span className="text-slate-400 text-sm">
                已选择 {tempSelectedTechStacks.length} 个技能
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => setTempSelectedTechStacks([])}
                  className="px-4 py-2 text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                >
                  清空
                </button>
                <button
                  onClick={() => {
                    setSelectedTechStacks(tempSelectedTechStacks);
                    setShowTechStackModal(false);
                    setTempSelectedTechStacks([]);
                  }}
                  className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-lg transition-colors cursor-pointer"
                >
                  确定
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 自定义滚动条样式 */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
      `}</style>

      {/* 修改密码弹窗 */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* 简历上传弹窗 */}
      <ResumeUploadModal
        isOpen={showResumeUploadModal}
        onClose={() => setShowResumeUploadModal(false)}
        onFileSelect={handleResumeUploadSuccess}
      />
    </div>
  );
}
